import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Camera, ImageIcon, Loader2, MapPin, CheckCircle2, Router as RouterIcon, Wifi, Star, ArrowDown, ArrowUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicRegistration = () => {
  const { slug } = useParams();
  const [branch, setBranch] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gpsFetching, setGpsFetching] = useState(false);
  const [uploadingKtp, setUploadingKtp] = useState(false);
  const [uploadingHouse, setUploadingHouse] = useState(false);

  const ktpInputRef = useRef(null);
  const ktpGalleryRef = useRef(null);
  const houseInputRef = useRef(null);
  const houseGalleryRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    nik: '',
    latitude: '',
    longitude: '',
    ktp_photo_url: '',
    house_photo_url: '',
    service_plan_id: '',
    notes: '',
  });

  useEffect(() => {
    document.title = 'Pendaftaran Pelanggan Baru';
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/register/${slug}/info`);
        setBranch(res.data?.branch);
        setPlans(res.data?.service_plans || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Cabang tidak ditemukan');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const fetchGps = () => {
    if (!navigator.geolocation) {
      toast.error('Browser tidak mendukung geolocation');
      return;
    }
    setGpsFetching(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
        }));
        toast.success('Lokasi berhasil diambil');
        setGpsFetching(false);
      },
      (err) => {
        toast.error('Gagal ambil lokasi: ' + err.message);
        setGpsFetching(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const uploadPhoto = async (file, kind) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5 MB');
      return;
    }
    const setUploading = kind === 'ktp' ? setUploadingKtp : setUploadingHouse;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      const res = await axios.post(
        `${API_URL}/api/public/register/upload-photo`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (res.data?.url) {
        const key = kind === 'ktp' ? 'ktp_photo_url' : 'house_photo_url';
        setForm((f) => ({ ...f, [key]: res.data.url }));
        toast.success(`Foto ${kind === 'ktp' ? 'KTP' : 'rumah'} berhasil diunggah`);
      }
    } catch (err) {
      toast.error('Upload gagal: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error('Nama, No.HP dan Alamat wajib diisi');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      const res = await axios.post(
        `${API_URL}/api/public/register/${slug}`,
        fd,
      );
      if (res.data?.success) {
        setSubmitted(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
        <div className="max-w-md text-center bg-white rounded-lg border border-slate-300 p-6">
          <h1 className="text-lg font-bold text-slate-800">Cabang Tidak Ditemukan</h1>
          <p className="text-sm text-slate-600 mt-2">
            URL pendaftaran tidak valid atau cabang sudah tidak aktif.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Toaster position="top-center" />
        <div className="max-w-md w-full bg-white rounded-lg border border-emerald-300 shadow-md p-8 text-center" data-testid="registration-success">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
          <h1 className="text-xl font-bold text-slate-800 mt-4">
            Pendaftaran Berhasil Diterima
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Terima kasih telah mendaftar di <b>{branch.name}</b>. Tim admin akan segera menghubungi Anda melalui nomor <b>{form.phone}</b> untuk konfirmasi & survey lokasi.
          </p>
          <p className="text-xs text-slate-400 mt-4">
            Simpan halaman ini sebagai bukti pendaftaran.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-3" data-testid="public-registration-page">
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg p-5 text-white mb-4">
          <div className="flex items-center gap-3">
            <RouterIcon className="w-8 h-8" />
            <div>
              <div className="text-xs uppercase tracking-wide opacity-80">Pendaftaran Cabang</div>
              <h1 className="text-lg sm:text-xl font-bold">{branch.name}</h1>
              {branch.city && <div className="text-xs opacity-90">{branch.city}</div>}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-slate-300 shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-2">
            Data Diri
          </h2>

          <div>
            <Label>Nama Lengkap *</Label>
            <Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Sesuai KTP" data-testid="name-input" maxLength={150} required />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>No. WhatsApp *</Label>
              <Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} placeholder="08xxxxxxxxxx" data-testid="phone-input" maxLength={30} required />
            </div>
            <div>
              <Label>Email (opsional)</Label>
              <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} placeholder="email@contoh.com" data-testid="email-input" maxLength={150} />
            </div>
          </div>

          <div>
            <Label>Alamat Lengkap *</Label>
            <Textarea value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})} placeholder="Jl., RT/RW, Kelurahan, Kecamatan" data-testid="address-input" required rows={3} />
          </div>

          <div>
            <Label>NIK (KTP)</Label>
            <Input
              value={form.nik}
              onChange={(e)=>setForm({...form, nik: e.target.value.replace(/\D/g, '').slice(0, 16)})}
              placeholder="16 digit NIK"
              data-testid="nik-input"
              inputMode="numeric"
              maxLength={16}
            />
            {form.nik && form.nik.length > 0 && form.nik.length < 16 && (
              <p className="text-[10px] text-amber-600 mt-1">NIK harus tepat 16 digit ({form.nik.length}/16)</p>
            )}
          </div>

          <div className="rounded-lg border border-slate-300 p-3 bg-slate-50 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <Label className="text-slate-700">Titik Koordinat Rumah</Label>
                <div className="text-xs text-slate-500 mt-0.5">
                  {form.latitude ? `${form.latitude}, ${form.longitude}` : 'Belum diambil'}
                </div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={fetchGps} disabled={gpsFetching} data-testid="gps-btn">
                {gpsFetching ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <MapPin className="w-4 h-4 mr-1"/>}
                Ambil Lokasi
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Latitude</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.latitude}
                  onChange={(e)=>setForm({...form, latitude: e.target.value.replace(/[^0-9.\-]/g,'')})}
                  placeholder="-6.4025"
                  data-testid="lat-input"
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Longitude</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.longitude}
                  onChange={(e)=>setForm({...form, longitude: e.target.value.replace(/[^0-9.\-]/g,'')})}
                  placeholder="106.8163"
                  data-testid="lng-input"
                  className="text-sm"
                />
              </div>
            </div>
            <p className="text-[10px] text-slate-500">
              Opsional. Bisa isi otomatis via tombol "Ambil Lokasi", atau tempel manual dari Google Maps
              (klik-kanan di titik → salin koordinat).
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'ktp', label: 'Foto KTP', urlKey: 'ktp_photo_url', ref: ktpInputRef, galleryRef: ktpGalleryRef, uploading: uploadingKtp },
              { key: 'rumah', label: 'Foto Rumah', urlKey: 'house_photo_url', ref: houseInputRef, galleryRef: houseGalleryRef, uploading: uploadingHouse },
            ].map((cfg) => (
              <div key={cfg.key} className="rounded-lg border border-slate-300 p-3">
                <Label className="text-slate-700">{cfg.label}</Label>
                <div className="mt-2">
                  {form[cfg.urlKey] ? (
                    <div className="relative">
                      <img
                        src={form[cfg.urlKey].startsWith('http') ? form[cfg.urlKey] : `${API_URL}${form[cfg.urlKey]}`}
                        alt={cfg.label}
                        className="w-full h-32 object-cover rounded"
                        data-testid={`${cfg.key}-preview`}
                      />
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-white/90 rounded px-2 py-0.5 text-xs shadow"
                        onClick={() => setForm({...form, [cfg.urlKey]: ''})}
                        data-testid={`${cfg.key}-remove`}
                      >
                        Ganti
                      </button>
                    </div>
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center rounded border border-dashed border-slate-300 text-slate-400">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                  {/* Camera input (HP) */}
                  <input
                    ref={cfg.ref}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    data-testid={`${cfg.key}-input`}
                    onChange={(e) => { uploadPhoto(e.target.files?.[0], cfg.key); e.target.value=''; }}
                  />
                  {/* Gallery/File input (no capture) */}
                  <input
                    ref={cfg.galleryRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`${cfg.key}-gallery-input`}
                    onChange={(e) => { uploadPhoto(e.target.files?.[0], cfg.key); e.target.value=''; }}
                  />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => cfg.ref.current?.click()}
                      disabled={cfg.uploading}
                      data-testid={`${cfg.key}-btn-camera`}
                    >
                      {cfg.uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Camera className="w-4 h-4 mr-1"/>}
                      Ambil Foto
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => cfg.galleryRef.current?.click()}
                      disabled={cfg.uploading}
                      data-testid={`${cfg.key}-btn-gallery`}
                    >
                      {cfg.uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <ImageIcon className="w-4 h-4 mr-1"/>}
                      Pilih Galeri
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {plans.length > 0 && (
          <>
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-2 pt-4">
            Paket Layanan (opsional)
          </h2>
          <div>
            <Label>Pilih Paket yang Diminati</Label>
            <Select
              value={form.service_plan_id || 'none'}
              onValueChange={(v) => setForm({ ...form, service_plan_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger data-testid="plan-select">
                <SelectValue placeholder="— Belum tahu / Konsultasikan ke admin —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Belum tahu / Konsultasi ke admin —</SelectItem>
                {plans.map((p) => {
                  const isShared = p.branch_id == null;
                  return (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.is_featured ? '⭐ ' : ''}{p.name} {p.speed_down ? `(${p.speed_down})` : ''} — Rp {Number(p.price || 0).toLocaleString('id-ID')}{isShared ? ' · Umum' : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {/* Interactive preview card */}
            {(() => {
              const selected = plans.find((p) => String(p.id) === String(form.service_plan_id));
              if (!selected) return null;
              return (
                <div className="mt-3 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 relative overflow-hidden">
                  {selected.is_featured && (
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 shadow-sm">
                      <Star className="w-3 h-3 fill-amber-800" /> REKOMENDASI
                    </span>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center flex-shrink-0 shadow">
                      <Wifi className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 leading-tight truncate">{selected.name}</h3>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {selected.speed_down && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <ArrowDown className="w-3 h-3" /> {selected.speed_down}
                          </span>
                        )}
                        {selected.speed_up && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-sky-100 text-sky-700 border border-sky-200">
                            <ArrowUp className="w-3 h-3" /> {selected.speed_up}
                          </span>
                        )}
                      </div>
                      {selected.description && (
                        <p className="text-xs text-slate-600 mt-2 leading-relaxed">{selected.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-indigo-100 flex items-baseline gap-1">
                    <span className="text-[11px] text-slate-500">Rp</span>
                    <span className="text-2xl font-extrabold text-indigo-700 tracking-tight">
                      {Number(selected.price || 0).toLocaleString('id-ID')}
                    </span>
                    <span className="text-xs text-slate-500 ml-1">/ bulan</span>
                  </div>
                </div>
              );
            })()}
          </div>
          </>
          )}

          <div>
            <Label>Catatan / Pertanyaan (opsional)</Label>
            <Textarea value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} placeholder="Contoh: minta dipasang secepatnya, atau ada pertanyaan..." rows={2} data-testid="notes-input" />
          </div>

          <Button type="submit" className="w-full text-base py-6" disabled={submitting} data-testid="submit-btn">
            {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin"/>Mengirim...</> : 'Kirim Pendaftaran'}
          </Button>

          <p className="text-xs text-center text-slate-500">
            Data Anda hanya digunakan untuk keperluan pendaftaran & survey. Tim <b>{branch.name}</b> akan menghubungi Anda dalam 1x24 jam.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PublicRegistration;
